<script setup lang="ts">
import {computed} from "vue";
import {ChatMessage, ChatModes} from "@/store/chats";
import moment from "moment/moment";
import ChatMessageAttached from "@/components/main/chat-panel/ChatMessageAttached.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import {applyChatMessagePlaceholders} from "@/utils/textHelpers";
import {useChatMessageLinks} from "@/composables/useChatMessageLinks";

const props = defineProps<{
	message: ChatMessage,
	mode: ChatModes,
	selected: boolean
}>()

const emit = defineEmits<{
	(e: 'set-answered', value: number): void
	(e: 'open-bill', value: number): void
	(e: 'navigated'): void
}>()

const getDialogDate = (date: string) => moment(date).format("DD.MM.YYYY HH:mm");

// подстановка плейсхолдеров обязательно до замены переносов на <br>
const messageHtml = computed(() =>
	applyChatMessagePlaceholders(props.message?.Text ?? '', props.message?.Links, props.message?.Bills)
		.replaceAll('\n', '<br>')
)

// обработчик висит на всём теле сообщения, поэтому покрывает и вложенные сообщения
const { onMessageClick } = useChatMessageLinks({
	onOpenBill: (billId: number) => emit('open-bill', billId),
	onNavigated: () => emit('navigated'),
})
</script>

<template>
	<div
		:id="`message-${props.message.Id}`"
		:class="{
			'chat-messages-panel__messages-block': true,
			'chat-messages-panel__messages-block--searched': props.mode === 'search-messages'
		}"
	>
		<img
			v-if="props.message.PhotoLink"
			v-img-fade="props.message.PhotoLink"
			class="chat-messages-panel__messages-block-logo-img"
			alt="Avatar"
		/>
		<div
			v-else
			class="chat-messages-panel__messages-block-logo-text"
		>
			{{ props.message.PhotoLiter }}
		</div>
		<div
			:class="{
				'chat-messages-panel__messages-block-body': true,
				'chat-messages-panel__messages-block-body--selected': props.selected,
				'chat-messages-panel__messages-block-body--unreaded': props.message.Unreaded,
			}"
		>
			<div class="body-header">
				<div class="body-header__title">
					<div class="body-header__title-name">{{ props.message.WorkerName }}</div>
					<div class="body-header__title-date">{{ getDialogDate(props.message.Date) }}</div>
				</div>
				<app-tooltip :text="$t('chat_reply')">
					<template #activator>
						<app-icon
							class="body-header__icon-reply"
							name="reply-left"
							width="14px"
							color="#D2D2D2"
							hover-color="#3F884F"
							cursor="pointer"
							@click.stop="emit('set-answered', props.message.Id)"
						/>
					</template>
				</app-tooltip>
			</div>
			<div
				class="body-message"
				@click="onMessageClick"
			>
				<div
					:class="{
							'body-message__text': true,
							'body-message__text--is-system': props.message.IsSystem
						}"
					v-html="messageHtml"
				/>
				<div
					v-if="props.message.AttachedMessages?.length"
					class="body-message__attacheds"
				>
					<chat-message-attached
						v-for="aMessage in props.message.AttachedMessages"
						:attached-message="aMessage"
						:key="aMessage.Id"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.chat-messages-panel__messages-block {
	display: flex;
	gap: 4px;
	margin: 0 8px;
	border-radius: 4px;
	cursor: pointer;
	transition: background-color 0.5s ease-in-out;

	&:hover {
		.body-header__icon-reply {
			opacity: 1;
		}
	}

	&--searched {
		&:hover {
			background-color: #F3F8F4 !important;
		}
	}

	&-logo {
		display: flex;
		align-items: center;
		height: 30px;
		width: 30px;

		&-img,
		&-text {
			height: 30px;
			min-width: 30px;
			max-width: 30px;
			border-radius: 50%;
		}

		&-text {
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 18px;
			line-height: 18px;
			text-transform: uppercase;
			color: #fff;
			background-color: #E6C15A;
		}
	}

	&-body {
		display: flex;
		flex-direction: column;
		width: 100%;
		padding: 0 4px;
		border-radius: 6px;
		border: 1px solid transparent;
		color: #4D4D4D;

		&--selected {
			background-color: #F3F8F4 !important;
		}

		&--unreaded {
			background-color: #F2F2F2;
		}

		.body-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			margin-bottom: 2px;

			&__title {
				display: flex;
				align-items: center;
				gap: 4px;

				&-name {
					font-size: 14px;
					line-height: 18px;
					font-weight: 500;
					color: #4D4D4D;
				}

				&-date {
					font-size: 12px;
					line-height: 16px;
					color: #979797;
				}
			}

			&__icon-reply {
				font-size: 16px;
				opacity: 0;
				transition: opacity 0.1s ease-in;

				&:hover {
					color: #499259;
				}
			}
		}

		.body-message {
			display: flex;
			flex-direction: column;

			&__text {
				font-size: 14px;
				line-height: 18px;
				color: #4D4D4D;
				white-space: pre-wrap;
				word-break: break-word;

				&--is-system {
					color: #979797 !important;
				}
			}
		}
	}
}
</style>
